import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Text } from '../Typography';
import {
  FieldGrid,
  FieldHint,
  FieldLabel,
  FieldWrapper,
  PrimaryButton,
  ProcessingButton,
  Section,
  SignatureHeading,
  VarTextField,
} from './signerStyles';

/** Turn an UPPER_SNAKE_CASE token into a human label, e.g. COUNTRY_TRUST_NAME → "Country Trust Name". */
export const humanizeToken = (token: string): string =>
  token
    .toLowerCase()
    .split('_')
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : word))
    .join(' ');

const truncateAddress = (addr: string) =>
  addr.length > 12 ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : addr;

const ErrorNote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ backgroundColor: '#ffebee', padding: '12px 16px', borderRadius: '8px', mt: 2 }}>
    <Text small sx={{ color: '#c62828', wordBreak: 'break-word' }}>
      {children}
    </Text>
  </Box>
);

interface TemplateVarsFormProps {
  /** Heading shown above the grid. */
  heading?: string;
  /** Supporting copy under the heading. */
  description?: string;
  /** All placeholder names for the document type. */
  placeholders: string[];
  /** Current input values (drafts), keyed by token. */
  values: Record<string, string>;
  /** Values already persisted on the server, keyed by token. */
  templateVars: Record<string, string>;
  /** Maps each filled token to the lowercase wallet that filled it. */
  owners?: Record<string, string>;
  /** Connected wallet, lowercase. */
  currentAccount: string;
  /** When false, every field is read-only (e.g. a viewer who is neither creator nor signer). */
  canEdit: boolean;
  /** Spinner shown while placeholders are being fetched. */
  loading?: boolean;
  onChange: (key: string, value: string) => void;
  /** Omit to hide the save button (e.g. the create form saves on submit instead). */
  onSave?: () => void;
  saving?: boolean;
  saveError?: string | null;
  /** Keys that are always disabled regardless of ownership (e.g. caller-prefilled values). */
  lockedKeys?: string[];
}

/**
 * Renders the document's template placeholders as a grid of inputs. Fields filled by another
 * party are locked (disabled) and annotated with the owning wallet, so a signatory can see — but
 * not overwrite — values supplied by the creator or other signers. See IPFS_DOCUMENT_MODULE.md §5
 * (PATCH /:id/template-vars).
 */
const TemplateVarsForm: React.FC<TemplateVarsFormProps> = ({
  heading = 'Document Details',
  description,
  placeholders,
  values,
  templateVars,
  owners = {},
  currentAccount,
  canEdit,
  loading = false,
  onChange,
  onSave,
  saving = false,
  saveError,
  lockedKeys = [],
}) => {
  if (loading) {
    return (
      <Section>
        <SignatureHeading>{heading}</SignatureHeading>
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
          <CircularProgress size={24} />
        </Box>
      </Section>
    );
  }

  if (placeholders.length === 0) {
    return null;
  }

  const account = currentAccount.toLowerCase();
  const ownerOf = (key: string) => owners[key]?.toLowerCase();
  const isLockedByOther = (key: string) => {
    const owner = ownerOf(key);
    return !!owner && owner !== account;
  };

  // A field is editable when the form allows editing, is not locked by another party, and is not
  // an explicitly locked key (e.g. pre-filled by the caller).
  const isEditable = (key: string) => canEdit && !isLockedByOther(key) && !lockedKeys.includes(key);

  const remaining = placeholders.filter((key) => !(templateVars[key] ?? '').trim()).length;
  const hasEditableField = canEdit && placeholders.some((key) => isEditable(key));

  return (
    <Section>
      <SignatureHeading>{heading}</SignatureHeading>
      {description && (
        <Text small sx={{ color: '#666', mt: -1, mb: 2, lineHeight: '18px' }}>
          {description}
        </Text>
      )}

      <FieldGrid>
        {placeholders.map((key) => {
          const lockedByOther = isLockedByOther(key);
          const editable = isEditable(key);
          const owner = ownerOf(key);
          const filledByMe = owner === account && !!owner;

          return (
            <FieldWrapper key={key}>
              <FieldLabel htmlFor={`var-${key}`}>
                {humanizeToken(key)}
                {lockedByOther && <LockOutlinedIcon sx={{ fontSize: 14, color: '#999' }} />}
              </FieldLabel>
              <VarTextField
                id={`var-${key}`}
                value={values[key] ?? ''}
                onChange={(e) => onChange(key, e.target.value)}
                disabled={!editable}
                placeholder={editable ? humanizeToken(key) : '—'}
                size="small"
                fullWidth
              />
              {lockedByOther && owner && (
                <FieldHint>Filled by {truncateAddress(owner)}</FieldHint>
              )}
              {filledByMe && <FieldHint>Filled by you</FieldHint>}
            </FieldWrapper>
          );
        })}
      </FieldGrid>

      {canEdit && (
        <Text small sx={{ color: remaining === 0 ? '#15803d' : '#888', fontSize: '12px', mt: 2 }}>
          {remaining === 0
            ? 'All fields filled.'
            : `${remaining} field${remaining === 1 ? '' : 's'} still need a value before the document can be finalized.`}
        </Text>
      )}

      {onSave && hasEditableField && (
        <Box sx={{ mt: 2 }}>
          {saving ? (
            <ProcessingButton disabled variant="contained">
              <CircularProgress size={20} sx={{ color: 'white' }} />
            </ProcessingButton>
          ) : (
            <PrimaryButton onClick={onSave} variant="contained">
              Save Fields
            </PrimaryButton>
          )}
        </Box>
      )}

      {saveError && <ErrorNote>{saveError}</ErrorNote>}
    </Section>
  );
};

export default TemplateVarsForm;
